import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CreditCard, 
  Eye, 
  Pause, 
  Play, 
  AlertTriangle,
  ExternalLink,
  MoreVertical,
  Calendar,
  Activity
} from 'lucide-react';

import { WebIDData, toggleWebIDStatus, reportWebIDLost } from '../../services/webids';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui';
import { CopyText } from '../ui/CopyButton';

/**
 * @component WebIDCard
 * @description Tarjeta de WebID para mostrar en el dashboard
 */

interface WebIDCardProps {
  webId: WebIDData;
  onUpdate: () => void;
}

const WebIDCard: React.FC<WebIDCardProps> = ({ webId, onUpdate }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const getStatusColor = (status: WebIDData['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'assigned':
        return 'bg-blue-100 text-blue-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'lost':
        return 'bg-red-100 text-red-800';
      case 'unclaimed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: WebIDData['status']) => {
    switch (status) {
      case 'active':
        return 'Activa';
      case 'assigned':
        return 'Asignada';
      case 'paused':
        return 'Pausada';
      case 'lost':
        return 'Perdida';
      case 'unclaimed':
        return 'Sin activar';
      default:
        return 'Desconocido';
    }
  };

  const handleToggleStatus = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const newStatus = webId.status === 'active' ? 'paused' : 'active';
      await toggleWebIDStatus(webId.id, newStatus, user.uid);
      onUpdate();
    } catch (error) {
      console.error('Error toggling WebID status:', error);
      alert('Error al cambiar el estado de la pulsera');
    } finally {
      setLoading(false);
      setShowMenu(false);
    }
  };

  const handleReportLost = async () => {
    if (!user) return;

    const confirmed = window.confirm(
      '¿Estás seguro de que quieres reportar esta pulsera como perdida? ' +
      'Esto desactivará el acceso público hasta que la recuperes.'
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      await reportWebIDLost(webId.id, user.uid);
      onUpdate();
    } catch (error) {
      console.error('Error reporting WebID lost:', error);
      alert('Error al reportar la pulsera como perdida');
    } finally {
      setLoading(false);
      setShowMenu(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Nunca';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow relative">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <CreditCard className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {webId.emergencyProfile.fullName || 'Sin nombre'}
            </h3>
            <CopyText
              text={webId.id}
              truncate={true}
              maxLength={16}
              textClassName="text-sm text-gray-500"
              buttonVariant="ghost"
              className="mt-1"
              onCopy={() => console.log(`WebID ${webId.id} copiado`)}
            />
          </div>
        </div>

        {/* Status Badge */}
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(webId.status)}`}>
          {getStatusText(webId.status)}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {webId.scanCount}
          </div>
          <div className="text-xs text-gray-500">Escaneos</div>
        </div>
        <div className="text-center">
          <div className="text-sm font-medium text-gray-900">
            {formatDate(webId.lastScanned)}
          </div>
          <div className="text-xs text-gray-500">Último escaneo</div>
        </div>
      </div>

      {/* Emergency Profile Info */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-700">
            Creada: {formatDate(webId.createdAt)}
          </span>
        </div>

        {/* Onboarding Status */}
        {!webId.onboardingCompleted && (webId.status === 'active' || webId.status === 'assigned') && (
          <div className="flex items-center gap-2 mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm text-yellow-700 font-medium">
              Configuración pendiente
            </span>
          </div>
        )}

        {webId.emergencyProfile.phone && (
          <div className="text-sm text-gray-600">
            📞 {webId.emergencyProfile.phone}
          </div>
        )}
        {webId.emergencyProfile.emergencyContacts && webId.emergencyProfile.emergencyContacts.length > 0 && (
          <div className="text-sm text-gray-600">
            🚨 {webId.emergencyProfile.emergencyContacts.find(c => c.isPrimary)?.name || webId.emergencyProfile.emergencyContacts[0]?.name}
            {webId.emergencyProfile.emergencyContacts.length > 1 && (
              <span className="text-gray-400 ml-1">
                (+{webId.emergencyProfile.emergencyContacts.length - 1} más)
              </span>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Link
          to={
            !webId.onboardingCompleted && (webId.status === 'active' || webId.status === 'assigned')
              ? `/onboarding/${webId.id}`
              : `/panel/webids/${webId.id}`
          }
          className="flex-1 inline-flex items-center justify-center px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
        >
          <Eye className="h-4 w-4 mr-2" />
          {!webId.onboardingCompleted && (webId.status === 'active' || webId.status === 'assigned')
            ? 'Configurar'
            : 'Ver detalles'
          }
        </Link>

        <Button
          variant="outline"
          size="sm"
          onClick={() => window.open(webId.publicUrl, '_blank')}
        >
          <ExternalLink className="h-4 w-4" />
        </Button>

        {/* Menu dropdown */}
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMenu(!showMenu)}
            disabled={loading}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>

          {showMenu && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              <div className="py-1">
                {webId.status === 'active' || webId.status === 'paused' ? (
                  <button
                    onClick={handleToggleStatus}
                    disabled={loading}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    {webId.status === 'active' ? (
                      <>
                        <Pause className="h-4 w-4" />
                        Pausar pulsera
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4" />
                        Activar pulsera
                      </>
                    )}
                  </button>
                ) : null}

                {webId.status !== 'lost' && (
                  <button
                    onClick={handleReportLost}
                    disabled={loading}
                    className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
                  >
                    <AlertTriangle className="h-4 w-4" />
                    Reportar como perdida
                  </button>
                )}

                <Link
                  to={`/w/${webId.id}`}
                  target="_blank"
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  onClick={() => setShowMenu(false)}
                >
                  <Activity className="h-4 w-4" />
                  Ver perfil público
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close menu */}
      {showMenu && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setShowMenu(false)}
        />
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      )}
    </div>
  );
};

export default WebIDCard;
